// ============================
// AI Memo & AI Minutes Models
// ============================
// Stored in: rooms/{roomId}/roomitem/{itemId}
// RoomItem.type = 'ai-memo' | 'ai-minutes'

// --- Memo Paragraph ---
export interface MemoParagraph {
    id: string;
    content: string;
    source: 'voice' | 'text';
    created: Date;
    audioUrl?: string;  // Firebase Storage URL for original voice recording
}

// --- AI Tag ---
export interface AiTag {
    name: string;
    isActive: boolean;     // toggle on/off
    isNew?: boolean;       // highlight as newly added (on edit)
    isRemoved?: boolean;   // strikethrough for removed tags (on edit)
    source?: 'ai' | 'manual';  // ai=AI 생성, manual=수동 추가
    createdAt: Date;
}

// --- Memo Category ---
export interface MemoCategory {
    id: string;
    name: string;
    icon: string;
    color: string;
}
// Category name translation helper
import { t } from '@lingui/macro';

export const getCategoryName = (id: string): string => {
    switch (id) {
        case 'work': return t`Work`;
        case 'people': return t`People`;
        case 'idea': return t`Ideas`;
        case 'todo': return t`To-Do`;
        case 'meeting': return t`Meeting`;
        case 'study': return t`Learning`;
        case 'daily': return t`Daily`;
        default: return id;
    }
};

// Default categories (call getDefaultCategories() to get translated names)
export const getDefaultCategories = (): MemoCategory[] => [
    { id: 'work', name: getCategoryName('work'), icon: '💼', color: '#6366f1' },
    { id: 'people', name: getCategoryName('people'), icon: '👤', color: '#3b82f6' },
    { id: 'idea', name: getCategoryName('idea'), icon: '💡', color: '#f59e0b' },
    { id: 'todo', name: getCategoryName('todo'), icon: '✅', color: '#22c55e' },
    { id: 'meeting', name: getCategoryName('meeting'), icon: '🗓️', color: '#8b5cf6' },
    { id: 'study', name: getCategoryName('study'), icon: '📚', color: '#06b6d4' },
    { id: 'daily', name: getCategoryName('daily'), icon: '🌿', color: '#84cc16' },
];

// Keep static reference for backward compat (will use ko as static fallback)
export const DEFAULT_MEMO_CATEGORIES: MemoCategory[] = [
    { id: 'work', name: '업무', icon: '💼', color: '#6366f1' },
    { id: 'people', name: '사람', icon: '👤', color: '#3b82f6' },
    { id: 'idea', name: '아이디어', icon: '💡', color: '#f59e0b' },
    { id: 'todo', name: '할 일', icon: '✅', color: '#22c55e' },
    { id: 'meeting', name: '회의', icon: '🗓️', color: '#8b5cf6' },
    { id: 'study', name: '배움', icon: '📚', color: '#06b6d4' },
    { id: 'daily', name: '일상', icon: '🌿', color: '#84cc16' },
];

// --- AI Memo Data (stored as RoomItem extended fields) ---
export interface AiMemoData {
    paragraphs: MemoParagraph[];
    category: string;           // category ID
    tags: AiTag[];
    summary?: string;           // AI-generated summary
    language?: string;          // detected language (ko/ja/en)
    duration?: number;          // total recording duration in seconds
    status: 'draft' | 'saved' | 'archived';
}

// --- Transcript Entry (for minutes) ---
export interface TranscriptEntry {
    id: string;
    speaker?: string;           // speaker name (manual or auto-detected)
    content: string;
    timestamp: number;          // seconds from start of recording
}

// --- Minutes Summary (AI-generated) ---
export interface MinutesSummary {
    agenda?: string;                                                          // meeting agenda
    attendees: string[];                                                      // attendee names
    keyDiscussions: { topic: string; detail: string; timestamp?: number }[];   // key discussion points (timestamp in seconds)
    decisions: string[];                                                      // decisions made
    actionItems: { task: string; assignee?: string; deadline?: string }[];    // action items
    pendingItems: string[];                                                   // unresolved items
}

// --- AI Minutes Data (stored as RoomItem extended fields) ---
export interface AiMinutesData {
    transcript: TranscriptEntry[];   // raw transcript
    minutes: MinutesSummary;         // AI-summarized minutes
    tags: AiTag[];
    language?: string;
    duration: number;                // recording duration in seconds
    status: 'recording' | 'processing' | 'completed';
}

// --- AI Memo View Type ---
export type AiMemoView = 'memos' | 'minutes';
